import TableMonitoring from "components/TableMonitoring"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Box, Button, Paper } from "@mui/material"
import DrawerHeader from "components/DrawerHeader"
import Notification from "components/Notification"
import TableHeaderDynamic from "components/TableMonitoring/Header/Dynamic/TableHeaderDynamic"
import TableRowDynamic from "components/TableMonitoring/Body/TableRowDynamic"
import { initializeStudy } from "redux/reducers/studyReducer"
import SingleChoiceDropdown from "components/Dropdowns/SingleChoiceDropdown"
import Loader from "components/Loader/Loader"
import { deleteAnswers, fetchMoreAnswersAction, initializeFormAnswers } from "redux/reducers/studyFormAnswersReducer"
import ErrorPage from "pages/ErrorPage"
import NoDataTableBody from "components/TableMonitoring/Body/NoDataTableBody"
import useSortedData from "hooks/useSortedData"
import usePagination from "hooks/usePagination"
import TableToolbar from "components/TableMonitoring/Toolbar/TableToolbar"
import DeleteDialog from "components/Dialogs/DeleteDialog/DeleteDialog"
import { AppColors } from "resources/AppColors"
import useSelection from "hooks/useSelection"
import DownloadDialog from "components/Dialogs/DownloadDialog/DownloadDialog"
import { selectForm } from "redux/reducers/selectedFormReducer"
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { CommonTexts, studiesTexts } from "locale/es"
import BreadCrumbs from "components/Breadcrumbs/Breadcrumbs"
import TableChartIcon from '@mui/icons-material/TableChart';
import { useNavigate } from "react-router-dom"

function StudyDashboard() {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showDownloadDialog, setShowDownloadDialog] = useState(false)

  const { data: studyData, loading: studyLoading, error: studyError } = useSelector(({ study }) => study)

  const { data: answersData, totalCount, loading: answersLoading, error: answersError } = useSelector(({ studyFormAnswers }) => studyFormAnswers)

  const { data: exportsData } = useSelector(({ formAnswersExport }) => formAnswersExport)

  const selectedCenterStudyId = useSelector(({ center }) => center)?.data?.studyId

  const selectedForm = useSelector(({ selectedForm }) => selectedForm)

  const dispatch = useDispatch()

  const navigate = useNavigate()

  useEffect(() => {
    if (!selectedCenterStudyId) {
      navigate('/dades-del-participant')
    }
  }, [selectedCenterStudyId])

  useEffect(() => {
    if (selectedCenterStudyId && !studyData) {
      dispatch(initializeStudy(selectedCenterStudyId))
    }
  }, [selectedCenterStudyId, studyData, dispatch])

  const {
    sortedData,
    sortColumn,
    sortDirection,
    handleRequestSort
  } = useSortedData(answersData.answers, "submitted_at", "asc", true);

  const {
    visibleRows,
    page,
    rowsPerPage,
    paginationComponent
  } = usePagination(0, 5, sortedData, totalCount, handleInitializeAnswers, handleFetchMoreAnswers);

  const {
    selectedItems,
    setSelectedItems,
    handleSelectAllClick,
    handleSelectOneClick,
    isSelected
  } = useSelection(answersData.answers)

  function handleInitializeAnswers(page, rowsPerPage) {
    if (selectedForm) {
      dispatch(initializeFormAnswers(selectedCenterStudyId, selectedForm, page + 1, rowsPerPage))
    }
  }

  function handleFetchMoreAnswers(page, rowsPerPage) {
    return dispatch(fetchMoreAnswersAction(selectedForm, page + 1, rowsPerPage))
  }

  function handleDelete() {
    setShowDeleteDialog(false)
    dispatch(deleteAnswers(selectedItems))
    setSelectedItems([])
  }

  function handleSelectedForm(e) {
    dispatch(selectForm(e.target.value))
    dispatch(initializeFormAnswers(selectedCenterStudyId, e.target.value, 1, rowsPerPage))
  }

  function handleShowDeleteDialog() {
    setShowDeleteDialog(!showDeleteDialog)
  }

  function handleShowDownloadDialog() {
    setShowDownloadDialog(!showDownloadDialog)
  }

  if (studyLoading) {
    return (
      <Loader message={studiesTexts.LOADING_FORMS} pt={30} />
    )
  }

  if (studyError) {
    return (
      <>
        <ErrorPage message={studyError.message} />
        <Notification message={studyError.message} type={"error"} />
      </>
    )
  }

  return (
    <>
      <Box pt={4} pb={12} px={3} width="100%">
        <DrawerHeader />
        <>
          <BreadCrumbs name={CommonTexts.STUDY} icon={<TableChartIcon sx={{ mr: 0.5 }} fontSize="inherit" />} />
          <Box py={4} display="flex" justifyContent="space-between" alignItems="center">
            {studyData &&
              <SingleChoiceDropdown
                label={studiesTexts.SELECT_FORM_DROPDOWN}
                value={selectedForm}
                handleValue={handleSelectedForm}
                items={studyData.forms}
                sx={{
                  bgcolor: 'white',
                  borderRadius: 3,
                  boxShadow: 5,
                }}
              />
            }
            <Button
              variant="contained"
              onClick={handleShowDownloadDialog}
              disabled={answersData.answers.length === 0 || exportsData.answers.length === 0}
              sx={{ width: { xs: 100, sm: 125 }, p: 1, ml: 2, bgcolor: AppColors.PRIMARY }}
            >
              <FileDownloadIcon sx={{ mr: 1 }} />
              {CommonTexts.EXPORT}
            </Button>
          </Box>
        </>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableToolbar
            title={studiesTexts.TABLE_TITLE}
            numSelected={selectedItems.length}
            handleShowDeleteDialog={handleShowDeleteDialog}
          />
          {answersLoading ?
            <>
              <Loader message={studiesTexts.LOADING_ANSWERS} pt={1} />
            </>
            :
            <>
              <TableMonitoring
                tableHeader={
                  <TableHeaderDynamic
                    columns={answersData.questions}
                    sortDirection={sortDirection}
                    sortColumn={sortColumn}
                    onSort={handleRequestSort}
                    onRequestSort={handleRequestSort}
                    numSelected={selectedItems.length}
                    rowCount={answersData.answers.length}
                    onSelectAllClick={handleSelectAllClick}
                  />
                }
              >
                {visibleRows?.length > 0 && visibleRows.map((answer) => (
                  <TableRowDynamic
                    key={answer.id.answer}
                    entity={answer}
                    columns={answersData.questions}
                    loaded={!answersLoading}
                    // actions={<DeleteMenuOption entityId={answer.id} />}
                    isItemSelected={isSelected(answer.id.answer)}
                    onSelectOneClick={handleSelectOneClick}
                  />
                ))}
              </TableMonitoring>
              {visibleRows?.length === 0 &&
                <NoDataTableBody
                  entity={selectedForm}
                  firstVisitMessage={studiesTexts.SELECT_FORM}
                  noDataMessage={studiesTexts.NO_ANSWERS}
                />
              }
            </>
          }
          {paginationComponent}
        </Paper>
      </Box>
      <DeleteDialog
        title={studiesTexts.DELETE_TITLE}
        show={showDeleteDialog}
        handleShow={handleShowDeleteDialog}
        text1={studiesTexts.DELETE_CONFIRM}
        cardTitle={selectedItems}
        buttonColor={AppColors.PRIMARY}
        handleSubmit={handleDelete}
        buttonText={CommonTexts.DELETE}
      />
      <DownloadDialog
        title={studiesTexts.DOWNLOAD_TITLE}
        show={showDownloadDialog}
        handleShow={handleShowDownloadDialog}
        tableData={answersData}
        formName={studyData?.forms.find(form => form.value === selectedForm)?.name}
      />
    </>
  )
}

export default StudyDashboard